<template>
    <section class="booking" v-if="dictionary != null">
        <div class="booking__main booking-panel">
            <div class="booking__customer-login"
                v-if="!isLoggedIn && showLoginStart && mitIDStatus == mitIDStatuses.unset">
                <p class="booking__headline">{{ dictionary.BookingFlowCustomerHeadline }}</p>
                <p>{{ dictionary.BookingFlowCustomerText }}</p>
                <div class="booking__input-group">
                    <small v-if="customerPhoneInput != null && !customerPhoneInputValid" class="p-error">{{
                        dictionary.BookingFlowCustomerMobileNumberValidationError
                        }}</small>
                    <InputText type="number" v-model="customerPhoneInput"
                        :class="{ 'p-invalid': customerPhoneInput != null && !customerPhoneInputValid }"
                        :placeholder="dictionary.BookingFlowCustomerExistenceCheckInputPlaceholder"
                        :aria-label="dictionary.BookingFlowCustomerExistenceCheckInputPlaceholder" />
                    <Button class="p-button-secondary p-button-rounded" @click="checkUserStatus()"
                        :disabled="!customerPhoneInputValid"
                        :label="dictionary.BookingFlowCustomerCheckUserButtonText"></Button>
                </div>
                <Login v-if="showLogin" :redirect="false" :initialIdentifier="customerInfo.Phone"
                    @loggedIn="loginCallback" />
            </div>

            <div v-if="mitIDStatus == mitIDStatuses.none && !customerInfo.MitIdCode" class="booking__mit-id">
                <div class="booking__mit-id-text">
                    <h2 class="booking__headline">{{ dictionary.BookingFlowMitIdHeadline }}</h2>
                    <p>{{ dictionary.BookingFlowMitIdText }}</p>
                </div>
                <button v-if="mitIDStatus != mitIDStatuses.excempted"
                    @click="() => { this.$emit('initMitID', { step: steps.customer }) }" type="button"
                    class="booking__mit-id-button">{{
                        dictionary.BookingFlowMitIdButtonLabel }} <img role="præsentation"
                        src="/assets/images/mitIdLogo.svg" alt="" height="15" width="55" /></button>
                <ul class="booking__mit-id-links" v-for="link in mitIdLinks" :key="link.Url">
                    <li><a :href="link.Url" :target="link.Target">{{ link.Title }}</a></li>
                </ul>
            </div>

            <div v-if="showCustomerForm && (mitIDStatus == mitIDStatuses.verified || customerInfo.MitIdCode) || (customerInfo.MitIdCode && isNewCustomer)"
                class="booking__customer-form">
                <p class="booking__headline">{{ dictionary.BookingFlowCustomerFormHeadline }}</p>
                <div class="form-grid grid">
                    <div class="field col-12">
                        <label for="name">{{ dictionary.BookingFlowCustomerInfoFirstNameLabel }}</label>
                        <InputText :disabled="customerHasHiddenInfo == false" id="name" type="text"
                            v-model="v$.customerInfo.Name.$model"
                            :class="{ 'p-invalid': v$.customerInfo.Name.$invalid }" />
                    </div>
                </div>
                <div class="form-grid grid">
                    <div class="field col">
                        <label for="address">{{ dictionary.BookingFlowCustomerInfoAddressLabel }}</label>
                        <InputText :disabled="disableAddressFields" id="address" type="text"
                            v-model="v$.customerInfo.Address.$model"
                            :class="{ 'p-invalid': v$.customerInfo.Address.$invalid }" />
                    </div>

                </div>
                <div class="form-grid grid">
                    <div class="field col-12 sm:col-6">
                        <label for="zipCode">{{ dictionary.BookingFlowCustomerInfoZipCodeLabel }}</label>
                        <InputText :disabled="disableAddressFields" type="number" id="zipCode"
                            v-model="v$.customerInfo.ZipCode.$model" autocomplete="off" />
                    </div>
                    <div class="field col-12 sm:col-6">
                        <label for="city">{{ dictionary.BookingFlowCustomerInfoCityLabel }}</label>
                        <InputText :disabled="disableAddressFields" id="city" type="text"
                            v-model="v$.customerInfo.City.$model" autocomplete="off" />
                    </div>
                </div>
                <div class="form-grid grid">
                    <div class="field col-12 sm:col-6">
                        <label for="email">{{ dictionary.BookingFlowCustomerInfoEmailLabel }}</label>
                        <InputText id="email" type="email" v-model="v$.customerInfo.Email.$model"
                            :class="{ 'p-invalid': v$.customerInfo.Email.$invalid || !validEmail }" />
                    </div>
                    <div class="field col-12 sm:col-6">
                        <label for="phone">{{ dictionary.BookingFlowCustomerInfoPhoneLabel }}</label>
                        <InputText id="phone" type="tel" :disabled="!isNewCustomer"
                            v-model="v$.customerInfo.Phone.$model"
                            :class="{ 'p-invalid': v$.customerInfo.Phone.$invalid || !validPhone }" />
                    </div>
                </div>
                <div class="form-grid grid">

                    <div class="field col-12 sm:col-6">
                        <label for="phoneNextOfKin">{{ dictionary.BookingFlowCustomerInfoPhoneNextOfKinLabel }}</label>
                        <InputText id="phoneNextOfKin" type="tel" v-model="v$.customerInfo.PhoneNextOfKin.$model"
                            :class="{ 'p-invalid': v$.customerInfo.PhoneNextOfKin.$invalid }" />
                    </div>

                    <div v-if="customerInfo.IsCompany" class="field col-12 sm:col-6">
                        <label for="companyName">{{ dictionary.BookingFlowCustomerCompanyNameLabel }}</label>
                        <InputText :disabled="true" id="companyName" type="text" v-model="customerInfo.CompanyName"
                            :class="{ 'p-invalid': v$.customerInfo.CompanyName.$invalid }" />
                    </div>

                </div>
                <div v-if="customerInfo.IsCompany" class="form-grid grid">

                    <div class="field col-12 sm:col-6">
                        <label for="cvr">{{ dictionary.BookingFlowCustomerCvrLabel }}</label>
                        <InputText :disabled=true id="cvr" v-model="customerInfo.Cvr"
                            :class="{ 'p-invalid': v$.customerInfo.Cvr.$invalid }" />
                    </div>

                    <div class="field col-12 sm:col-6">
                        <label for="contactPerson">{{ dictionary.BookingFlowCustomerContactPersonLabel }}</label>
                        <InputText id="contactPerson" type="text" v-model="customerInfo.ContactPerson"
                            :class="{ 'p-invalid': v$.customerInfo.ContactPerson.$invalid }" />
                    </div>

                </div>
                <div class="form-grid grid">
                    <div class="field col">

                        <button type="button" class="booking__text-button"
                            @click="() => { this.$emit('initMitID', { step: steps.customer }) }">{{
                                dictionary.BookingFlowCustomerUpdateFromMitIdButtonLabel
                            }}</button>
                    </div>
                </div>
                <div class="form-grid grid">
                    <div class="field col">
                        <div class="field-checkbox">
                            <Checkbox inputId="marketing" v-model="customerInfo.Marketing" :binary="true" />
                            <label for="marketing">{{ dictionary.BookingFlowCustomerFormMarketingConsent }}</label>
                        </div>
                    </div>
                </div>
                <div class="form-grid grid">
                    <div class="field col">
                        <div class="field-checkbox">
                            <Checkbox inputId="dataConsent" v-model="dataConsent" :binary="true"
                                :class="{ 'p-invalid': v$.dataConsent.$invalid }" />
                            <label for="dataConsent">{{ dictionary.BookingFlowCustomerFormDataConsent }}</label>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="isLoggedIn" style="text-align:right">
                <button type="button" class="booking__text-button" @click="logOutCurrentUser">{{
                    dictionary.BookingFlowCustomerLogOutButtonLabel
                    }}</button>
            </div>
        </div>

        <aside class="booking__aside">
            <div class="booking__basket booking-panel">
                <BookingBasket :department="department" :basket="basket" :basketSummary="basketSummary"  :padlockPrice="padlockPrice"
                    :invitationCode="invitationCode" :nextStep="nextStep"
                    :nextStepButtonText="dictionary.BookingFlowCustomerNextStepText"
                    @stepChangeOverridden="handleStepChange" :nextStepDisableOverride="disableNextStep"
                    :ignoreInheritedStepChangeHandler="true" :step="step" :steps="steps" v-bind="$attrs" />

                <BookingProgress :progress="progress"></BookingProgress>
                <Button class="p-button-secondary p-button-rounded booking__go-back"
                    @click="handleStepBackChange(steps.rooms)">{{ dictionary.BookingFlowGoBackButtonText }}</Button>
            </div>
            <div class="booking-panel booking-panel--solar-powered" v-if="department.SolarPowered">
                <SolarPowered></SolarPowered>
            </div>
            <div class="booking-panel">
                <Help></Help>
            </div>
        </aside>
        <Sidebar v-model:visible="showBasketOverlay" position="bottom">
            <BookingBasket :department="department" :basket="basket" :basketSummary="basketSummary" :padlockPrice="padlockPrice"
                :invitationCode="invitationCode" :nextStep="nextStep"
                :nextStepButtonText="dictionary.BookingFlowCustomerNextStepText" :step="step" :steps="steps"
                @stepChangeOverridden="handleStepChange" :ignoreInheritedStepChangeHandler="true" v-bind="$attrs" />
            <Button class="p-button-secondary p-button-rounded p-button-sm booking__go-back"
                @click="handleStepBackChange(steps.rooms)">{{ dictionary.BookingFlowGoBackButtonText }}</Button>
        </Sidebar>
        <div class="booking-mobile-bar">
            <nav class="booking-mobile-bar__navigation">
                <Button class="p-button-text p-button-rounded p-button-sm booking-mobile-bar__basket-button"
                    @click="showBasketOverlay = true" icon="pi pi-list"
                    :label="`Se valgte rum (${basket.length})`"></Button>
                <Button :disabled="disableNextStep" class="p-button-primary p-button-rounded p-button-sm"
                    @click="handleStepChange(nextStep)">{{
                        dictionary.BookingFlowCustomerNextStepText }}</Button>

            </nav>
            <BookingProgress :progress="progress"></BookingProgress>

        </div>
    </section>
</template>
<script>

import bfdl from './../../bookingFlowDataLayer.js';
import BookingBasket from "../components/bookingBasket.vue";
import Checkbox from "primevue/checkbox";
import Button from "primevue/button";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import Help from './../components/help.vue';
import SolarPowered from './../components/solarPowered.vue';
import Login from './../components/login.vue';
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, email, integer, minLength, maxLength } from '@vuelidate/validators'
import Toast from 'primevue/toast';
import BookingProgress from './../components/bookingProgress.vue';
import Sidebar from 'primevue/sidebar';
import MitID from './../../mitId.js';
import vueHelpers from '../vueHelpers.js';

export default {
    name: "customer",
    components: {
        BookingBasket,
        InputNumber,
        Checkbox,
        InputText,
        Button,
        DataView,
        Help,
        SolarPowered,
        Toast,
        BookingProgress,
        Login,
        Sidebar
    },
    props: ["department", "progress", "basket","basketSummary", "padlockPrice", "existingUserObject", "invitationCode", "step", "steps", "nextStep", "referrer", "mitIDStatus", "mitIDStatuses", "isNewCustomer", "customerHasHiddenInfo", "customerMissesAddress"],
    setup() {
        return { v$: useVuelidate() }
    },
    data() {
        return {
            dictionary: null,
            customerPhoneInput: null,
            customerPhoneInputValid: false,
            showCustomerForm: false,
            showLoginStart: true,
            showLogin: false,
            dataConsent: false,
            submitting: false,
            showBasketOverlay: false,
            validEmail: true,
            validPhone: true,
            isLoggedIn: false,
            originalCustomerPhone: null,
            originalCustomerEmail: null,
            mitIdLinks: [],
            customerInfo: {
                Id: null,
                Name: null,
                Address: null,
                ZipCode: null,
                City: null,
                Email: null,
                Phone: null,
                PhoneNextOfKin: null,
                Marketing: true,
                IsCompany: false,
                Cvr: null,
                CompanyName: null,
                ContactPerson: null,
                MitIdCode: null
            }
        };
    },
    validations() {
        return {
            dataConsent: { checked: value => value === true },
            customerInfo: {
                Name: { required },
                Address: { required },
                ZipCode: {
                    required,
                    integer,
                    minLengthValue: minLength(4),
                    maxLengthValue: maxLength(4)
                },
                City: { required },
                Email: { required, email },
                Phone: {
                    required,
                    integer,
                    minLengthValue: minLength(8),
                    maxLengthValue: maxLength(8)
                },
                PhoneNextOfKin: {
                    required,
                    integer,
                    minLengthValue: minLength(8),
                    maxLengthValue: maxLength(8)
                },
                CompanyName: requiredIf(() => {
                    return this.customerInfo.IsCompany;
                }),
                Cvr: requiredIf(() => {
                    return this.customerInfo.IsCompany;
                }),
                ContactPerson: requiredIf(() => {
                    return this.customerInfo.IsCompany;
                })

            }
        }
    },
    created() {
        const vm = this;



        fetch("/umbraco/api/dictionaryapi/GetCustomerDictionary").then((response) => response.json())
            .then((data) => {
                vm.dictionary = data.dictionary;
                vm.mitIdLinks = data.mitIdLinks;
            })
            .catch((error) => {
                console.log(error);
            });
    },
    mounted() {
        const vm = this;

        bfdl.pushToDataLayer(
            bfdl.getEventName(vm.referrer),
            bfdl.steps.roomAdded,
            bfdl.getRoomListFromBasket(vm.basket,vm.department)
        );

        //A user object is passed from the parent
        if (vm.existingUserObject) {
            vm.customerInfo = vm.existingUserObject;
            vm.showCustomerForm = true;
            vm.showLoginStart = false;

            //No id, so it's a new customer
            if (vm.customerInfo.Id == null) {
                vm.$emit('customerInfoUpdated', { customer: vm.customerInfo, isNewCustomer: true });



            } else {
                vm.$emit('customerInfoUpdated', { customer: vm.customerInfo, isNewCustomer: false });

                //It's a returning customer, so get their MitIDStatus
                vm.originalCustomerEmail = vm.existingUserObject.Email
                vm.originalCustomerPhone = vm.existingUserObject.Phone
                vm.isLoggedIn = true;
                vm.$emit('setMitIDStatus');
            }

            //If it's a MitID callback, verify it.
            if (MitID.IsMitIDCallback()) {
                vm.$emit('verifyMitID', { step: vm.steps.customer });
            }

        } else {
            //There is no existing user object passes from the parent, so get it from the API
            vm.$emit("toggleSpinner", true);
            fetch("/umbraco/api/customerapi/GetCustomerProfile")
                .then((response) => response.json())
                .then((data) => {
                    if (data.status != 401 && data.Id !== null) {
                        vm.originalCustomerEmail = data.Email;
                        vm.originalCustomerPhone = data.Phone;
                        vm.customerInfo = data;
                        vm.showCustomerForm = true;
                        vm.isLoggedIn = true;

                        //If this is a MitID callback, verify it
                        if (MitID.IsMitIDCallback()) {
                            vm.$emit('verifyMitID', { step: vm.steps.customer });
                        } else {
                            //Else set the MitID Status
                            vm.$emit('setMitIDStatus');
                        }

                        //Push the customer data to the parent
                        vm.$emit('customerInfoUpdated', { customer: data, isNewCustomer: false });
                    } else {
                        //There was no logged in user, so it's a new customer, push that to the parents. 
                        vm.$emit('customerInfoUpdated', { customer: vm.customer, isNewCustomer: true });

                        //If it's a MitID callback, verify it.
                        if (MitID.IsMitIDCallback()) {
                            vm.$emit('verifyMitID', { step: vm.steps.customer });
                        }
                    }

                    vm.$emit("toggleSpinner", false);
                }).catch((error) => {
                    console.log(error);
                    vm.$emit("toggleSpinner", false);
                });
        }


    },
    methods: {
        resetCustomerInfo() {
            this.customerInfo = {
                Id: null,
                Name: null,
                Address: null,
                ZipCode: null,
                City: null,
                Email: null,
                Phone: null,
                PhoneNextOfKin: null,
                Marketing: true,
                IsCompany: false,
                Cvr: null,
                CompanyName: null,
                MitIdCode: null
            }
            this.originalCustomerEmail = null;
            this.originalCustomerPhone = null;

            this.$emit('customerInfoUpdated', { customer: this.customerInfo, isNewCustomer: null });
        },
        logOutCurrentUser() {
            const vm = this;
            fetch('/umbraco/api/loginapi/logoutuser')
                .then(response => {
                    vm.resetCustomerInfo();
                    vm.$emit('setMitIDStatus', vm.mitIDStatuses.unset);
                    vm.isLoggedIn = false;
                    vm.showLoginStart = true;
                    vm.showCustomerForm = false;
                })
                .catch(error => {
                    console.log(error);
                })
        },
        handleStepBackChange(nextStep) {
            this.$emit("stepChange", nextStep, true);
        },
        handleStepChange(nextStep) {
            const vm = this;
            vm.validEmail = true;
            if (!vm.v$.$invalid) {
                //Create or update the user
                vm.$emit("toggleSpinner", true);

                //Check if the phone and email exists
                fetch("/umbraco/api/customerapi/CustomerInfoAvailable?phoneNumber=" + vm.customerInfo.Phone + "&email=" + vm.customerInfo.Email)
                    .then(response => response.json())
                    .then((data) => {
                        //If the info is available
                        if ((data.Email || vm.customerInfo.Email == vm.originalCustomerEmail) && (data.Phone || vm.customerInfo.Phone == vm.originalCustomerPhone)) {
                            //Update existing customer
                            if (!vm.isNewCustomer) {
                                fetch("/umbraco/api/customerapi/updateCustomer", {
                                    method: "PUT",
                                    headers: {
                                        "Content-Type": "application/json"
                                    },
                                    body: JSON.stringify(vm.customerInfo)
                                }).then((response) => response.json())
                                    .then((data) => {
                                        bfdl.pushToDataLayer(bfdl.getEventName(vm.referrer), bfdl.steps.customerExisting);
                                        vm.$emit("toggleSpinner", false);

                                        //Make sure we retain the MitId Code
                                        data['MitIdCode'] = vm.customerInfo.MitIdCode;

                                        vm.$emit("customerInfoUpdated", { customer: data, isNewCustomer: vm.isNewCustomer });
                                        vm.$emit("stepChange", nextStep);
                                    }).catch((error) => {
                                        console.log(error);
                                        vm.$emit("toggleSpinner", false);
                                    });
                            } else {
                                //"Create the new custom"
                                bfdl.pushToDataLayer(bfdl.getEventName(vm.referrer), bfdl.steps.customerNew);
                                vm.$emit("toggleSpinner", false);
                                vm.$emit("customerInfoUpdated", { customer: vm.customerInfo, isNewCustomer: vm.isNewCustomer });
                                vm.$emit("stepChange", nextStep);
                            }

                        } else {
                            vm.$emit("toggleSpinner", false);
                            if (!data.Email) {
                                vm.validEmail = false;
                                vm.$toast.add({
                                    severity: 'error', closable: false, summary: vm.dictionary.BookingFlowCustomerEmailInUseErrorTitle, detail: vm.dictionary.BookingFlowCustomerEmailInUseErrorText, life: 10000
                                })
                            }
                            if (!data.Phone) {
                                vm.validPhone = false;
                                vm.$toast.add({
                                    severity: 'error', closable: false, summary: vm.dictionary.BookingFlowCustomerPhoneInUseErrorTitle, detail: vm.dictionary.BookingFlowCustomerPhoneInUseErrorText, life: 10000
                                })
                            }
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                        vm.$emit("toggleSpinner", false);
                    });
            } else {
                vm.$toast.add({
                    severity: 'error', closable: false, summary: vm.dictionary.BookingFlowCustomerMissingInfoErrorTitle, detail: vm.dictionary.BookingFlowCustomerMissingInfoErrorText, life: 10000
                })
            }
        },
        checkUserStatus() {
            const vm = this;
            vm.showLogin = false;
            vm.showCustomerForm = false;
            vm.$emit("toggleSpinner", true);

            fetch("/umbraco/api/customerapi/CustomerInfoAvailable?phoneNumber=" + vm.customerPhoneInput + "&onlyCheckPhone=true")
                .then(response => response.json())
                .then((data) => {
                    vm.customerInfo.Phone = vm.customerPhoneInput;
                    vm.customerPhoneInput = null;
                    if (!data.Phone) {
                        vm.showLogin = true;
                        vm.isNewCustomer = false;
                    } else {
                        vm.$emit('setMitIDStatus', vm.mitIDStatuses.none);
                        vm.showCustomerForm = true;
                        vm.$emit("customerInfoUpdated", { customer: vm.customerInfo, isNewCustomer: true });

                        vm.$toast.add({
                            severity: 'info', summary: vm.dictionary.BookingFlowCustomerUnknownUserNotificationTitle, detail: vm.dictionary.BookingFlowCustomerUnknownUserNotificationText, life: 10000
                        })

                    }
                    vm.$emit("toggleSpinner", false);
                })
                .catch((error) => {
                    console.log(error);
                    vm.$emit("toggleSpinner", false);
                });
        },
        loginCallback() {
            const vm = this;
            vm.$emit("toggleSpinner", true);

            fetch("/umbraco/api/customerapi/GetCustomerProfile")
                .then((response) => response.json())
                .then((data) => {
                    vm.showLogin = false;
                    vm.customerInfo = data;
                    vm.showCustomerForm = true;
                    vm.isLoggedIn = true;
                    vm.originalCustomerEmail = data.Email;
                    vm.originalCustomerPhone = data.Phone;
                    vm.$emit('customerInfoUpdated', { customer: data, isNewCustomer: null });
                    vm.$emit('setMitIDStatus');

                    vm.$emit("toggleSpinner", false);

                }).catch((error) => {
                    console.log(error);
                    vm.$emit("toggleSpinner", false);

                });
        },
    },
    computed: {
        disableNextStep() {
            return ((this.mitIDStatus != this.mitIDStatuses.verified && this.customerInfo.MitIdCode == null) || (this.mitIDStatus == this.mitIDStatuses.verified && this.basket.length == 0));
        },
        disableAddressFields() {
            const vm = this;
            return vm.customerHasHiddenInfo == false && !vm.customerMissesAddress;
        }
    },
    watch: {
        customerPhoneInput(newValue, oldValue) {
            const vm = this;
            if (newValue == null) {
                vm.customerPhoneInputValid = true;
            } else {
                const regex = new RegExp('\\d{8}');
                const regexResult = regex.test(newValue);
                vm.customerPhoneInputValid = regexResult && newValue < 100000000;

            }
        },
        'customerInfo.PhoneNextOfKin'(newValue) {
            if (newValue != null) {
                this.$nextTick(() => {
                    this.customerInfo.PhoneNextOfKin = newValue.trim();
                });
            }
        },
        'customerInfo.Email'(newValue) {
            if (newValue != null) {
                this.$nextTick(() => {
                    this.customerInfo.Email = newValue.trim();
                });
            }
        },
        'customerInfo.Phone'(newValue) {
            if (newValue != null) {
                this.$nextTick(() => {
                    this.customerInfo.Phone = newValue.trim();
                });
            }
        }
    }
};
</script>
